import { ref, computed } from 'vue';
import type { Auth0VueClient } from '@auth0/auth0-vue';
import { extractRolesFromToken, hasAdminRole } from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';

interface AuthMode {
  localOnly: boolean;
  requiresAuth: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    sub: string;
    email?: string;
    name?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // Allow additional properties from Auth0
  } | null;
}

const authMode = ref<AuthMode | null>(null);
const localToken = ref<string | null>(null);
const authState = ref<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
});

// Store Auth0 instance at module level
let auth0Instance: Auth0VueClient | null = null;

/**
 * Composable for handling authentication logic
 * Supports both local-only mode and Auth0 authentication
 */
export function useAuth(): {
  isAuthenticated: import('vue').ComputedRef<boolean>;
  isLoading: import('vue').ComputedRef<boolean>;
  user: import('vue').ComputedRef<AuthState['user']>;
  isLocalMode: import('vue').ComputedRef<boolean>;
  isAdmin: import('vue').ComputedRef<boolean>;
  setAuth0Instance: (instance: Auth0VueClient | null) => void;
  initialize: () => Promise<void>;
  getToken: () => Promise<string | null>;
  login: () => Promise<void>;
  signup: () => Promise<void>;
  logout: () => Promise<void>;
} {
  // Only use Auth0 in non-local mode
  const isLocalMode = computed(() => authMode.value?.localOnly ?? false);

  /**
   * Check if current user has admin role
   * Uses shared role checking logic from @maso-dj/shared-types
   */
  const isAdmin = computed(() => {
    if (!authState.value.user || isLocalMode.value) {
      return false;
    }

    const roles = extractRolesFromToken(authState.value.user);
    return hasAdminRole(roles);
  });

  /**
   * Set the Auth0 instance (must be called from component setup context)
   */
  const setAuth0Instance = (instance: Auth0VueClient | null): void => {
    if (instance) {
      auth0Instance = instance;
    }
  };

  /**
   * Initialize authentication by detecting mode and setting up accordingly
   */
  const initialize = async (): Promise<void> => {
    try {
      authState.value.isLoading = true;

      // Detect authentication mode
      const modeResponse = await fetch('/api/auth/mode');
      if (!modeResponse.ok) {
        throw new Error('Failed to detect authentication mode');
      }
      authMode.value = await modeResponse.json();

      if (authMode.value?.localOnly) {
        // Local-only mode: fetch token immediately
        const tokenResponse = await fetch('/api/auth/local-token');
        if (!tokenResponse.ok) {
          throw new Error('Failed to fetch local token');
        }
        const tokenData = await tokenResponse.json();
        localToken.value = tokenData.token;

        // Set authenticated state for local mode
        authState.value.isAuthenticated = true;
        authState.value.user = {
          sub: 'LOCALUSER',
          email: 'local@localhost',
          name: 'Local User',
        };
      } else {
        // Auth0 mode: wait for Auth0 to finish loading
        if (auth0Instance) {
          // Wait for Auth0 to finish processing (e.g., callback from redirect)
          while (auth0Instance.isLoading.value) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          authState.value.isAuthenticated = auth0Instance.isAuthenticated.value;
          authState.value.user = auth0Instance.user.value as AuthState['user'];

          // Try to get the access token and extract user info if needed
          if (authState.value.isAuthenticated) {
            try {
              const token = await auth0Instance.getAccessTokenSilently();

              // WORKAROUND: If user is undefined but we have a token, use the token's payload
              if (!authState.value.user && token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                authState.value.user = {
                  sub: payload.sub,
                  email: payload.email,
                  name: payload.name,
                  // Include the entire payload so custom claims are accessible
                  ...payload
                };
              }
            } catch (error) {
              logger.error('Failed to get access token:', error);
            }
          }
        } else {
          logger.error('Auth0 instance not set. Call setAuth0Instance() first.');
        }
      }
    } catch (error) {
      logger.error('Authentication initialization failed:', error);
      authState.value.isAuthenticated = false;
      authState.value.user = null;
    } finally {
      authState.value.isLoading = false;
    }
  };

  /**
   * Get the current authentication token
   * - In local mode, returns the local token
   * - In Auth0 mode, returns the Auth0 access token (with automatic refresh)
   * - If token refresh fails, logs out the user
   */
  const getToken = async (): Promise<string | null> => {
    if (isLocalMode.value) {
      return localToken.value;
    }

    if (auth0Instance) {
      try {
        return await auth0Instance.getAccessTokenSilently();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logger.error('Failed to get Auth0 token:', error);

        // Check if this is a "consent_required" or "login_required" error
        // These happen when returning from external redirects (e.g., Stripe)
        const errorCode = error?.error || error?.message || '';
        if (errorCode.includes('consent_required') || errorCode.includes('login_required')) {
          logger.info('Auth0 session expired or consent required - triggering re-authentication');

          // Try to re-authenticate with a popup or redirect
          try {
            const token = await auth0Instance.getAccessTokenWithPopup();
            return token || null;
          } catch {
            logger.warn('Popup authentication failed, will redirect to login');
            // If popup fails, trigger a redirect login
            await auth0Instance.loginWithRedirect({
              appState: {
                target: window.location.pathname + window.location.search,
              },
            });
            return null;
          }
        }

        // For other errors, log out
        logger.warn('Token refresh failed, logging out...');
        authState.value.isAuthenticated = false;
        authState.value.user = null;

        // Trigger Auth0 logout (will redirect to login)
        await auth0Instance.logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });

        return null;
      }
    }

    return null;
  };

  /**
   * Log in the user
   * - In local mode, this is a no-op (already authenticated)
   * - In Auth0 mode, redirects to Auth0 login page
   */
  const login = async (): Promise<void> => {
    if (isLocalMode.value) {
      logger.debug('Already authenticated in local mode');
      return;
    }

    if (auth0Instance) {
      await auth0Instance.loginWithRedirect({
        appState: {
          target: window.location.pathname,
        },
      });
    }
  };

  /**
   * Sign up a new user
   * - In local mode, this is a no-op (already authenticated)
   * - In Auth0 mode, redirects to Auth0 signup page
   */
  const signup = async (): Promise<void> => {
    if (isLocalMode.value) {
      logger.debug('Already authenticated in local mode');
      return;
    }

    if (auth0Instance) {
      await auth0Instance.loginWithRedirect({
        appState: {
          target: window.location.pathname,
        },
        authorizationParams: {
          screen_hint: 'signup',
        },
      });
    }
  };

  /**
   * Log out the user
   * - In local mode, this is a no-op (cannot log out)
   * - In Auth0 mode, logs out via Auth0
   */
  const logout = async (): Promise<void> => {
    if (isLocalMode.value) {
      logger.debug('Cannot log out in local mode');
      return;
    }

    if (auth0Instance) {
      await auth0Instance.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  return {
    // State
    isAuthenticated: computed(() => authState.value.isAuthenticated),
    isLoading: computed(() => authState.value.isLoading),
    user: computed(() => authState.value.user),
    isLocalMode,
    isAdmin,

    // Methods
    setAuth0Instance,
    initialize,
    getToken,
    login,
    signup,
    logout,
  };
}
